import {Routing} from 'nutshell-core/routing';
import type {User} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';
import {ChatbotIcon} from 'shells/icon/svg/chatbot-icon';
import {PeopleRegularIcon} from 'shells/icon/svg/people-regular-icon';
import {PersonRoundRegularIcon} from 'shells/icon/svg/person-round-regular-icon';
import {Stack} from 'shells/layout/stack/stack';
import type {SubmenuNavItem} from 'shells/navigation/types';
import {StatusDot} from 'shells/status-selector/status-dot';
import {Body} from 'shells/typography/body';

import type {
    GetInboxSidebarCounts_inboxThreadCounts_sms as SmsThreadCounts,
    GetInboxSidebarCounts_inboxThreadCounts_chat as ChatThreadCounts,
    GetInboxSidebarCounts_inboxThreadCounts_messenger as MessengerThreadCounts,
    GetInboxSidebarCounts_inboxThreadCounts_instagram as InstagramThreadCounts,
    GetInboxSidebarCounts_inboxThreadCounts_allChannels as AllChannelsThreadCounts,
    GetInboxSidebarCounts_inboxThreadCounts_whatsapp as WhatsAppThreadCounts,
    GetInboxSidebarCounts_inboxThreadCounts_email as EmailThreadCounts,
} from 'nutshell-graphql-types';

import {appHistory} from '../../history';

import styles from './add-inbox/add-inbox.module.css';

const periodFilter = () => {
    return Routing.param({filter: {period: '-d30'}});
};

const ownerFilter = (user: User) => {
    return Routing.param({
        filter: {
            owner: {
                anyAll: 'any',
                data: [{data: user.id}],
            },
        },
    });
};

export function getAgentMenuItem(activeAgentsCount: number, isAgentsSection: boolean) {
    return (
        <Stack isFullWidth={true} spacing={8} alignItems='center' isHorizontal={true}>
            <Body color={isAgentsSection ? 'dark' : 'white'} size='small'>
                Agents
            </Body>
            <StatusDot color={activeAgentsCount > 0 ? colors.success : colors.greyLt} size={8} />
        </Stack>
    );
}

export const getSettingsLinks = (
    userCanAccessSettings: boolean,
    isEmbedded: boolean
): SubmenuNavItem[] => {
    if (!userCanAccessSettings) {
        return [];
    }

    const links = [
        {id: 'chat-settings', label: 'Chat', to: '/inbox/settings/chat'},
        {id: 'sms-settings', label: 'SMS', to: '/inbox/settings/sms'},
        ...(NutClientConfig.hasSharedEmailInboxFF && !isEmbedded
            ? [{id: 'email-settings', label: 'Email', to: '/inbox/settings/email'}]
            : []),
        {id: 'facebook-settings', label: 'Facebook', to: '/inbox/settings/facebook'},
        {id: 'whatsapp-settings', label: 'WhatsApp', to: '/inbox/settings/whatsapp'},
    ];

    return links;
};

export const getChatLinks = (
    chatCounts: ChatThreadCounts | null | undefined,
    userCanAccessSettings: boolean,
    user?: User | null,
    options?: {excludeChatbot?: boolean}
): SubmenuNavItem[] => {
    const links: SubmenuNavItem[] = [];

    if (userCanAccessSettings) {
        links.push({
            id: 'chat-all',
            label: 'All',
            to: `/inbox/chat/open?${periodFilter()}`,
            count: chatCounts ? chatCounts.totalCount : 0,
            activeMatch: /^\/inbox\/chat\/(?!unassigned|chatbot)[\w-]+(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
            // @ts-expect-error
            icon: () => <PeopleRegularIcon size={16} color={colors.white} />,
        });
    }

    links.push({
        id: 'chat-unassigned',
        label: 'Unassigned',
        to: `/inbox/chat/unassigned?${periodFilter()}`,
        count: chatCounts ? chatCounts.unassignedCount : 0,
        activeMatch: /^\/inbox\/chat\/unassigned(?:\/.*)?$/,
        searchMatch: /filter%5Bperiod%5D=/,
        // @ts-expect-error
        icon: () => <PersonRoundRegularIcon size={19} color={colors.white} />,
    });

    if (user) {
        const userInboxCount =
            chatCounts &&
            chatCounts.userInboxCounts.find(
                (userInbox) => userInbox.user && userInbox.user.id === user.id
            );

        const isActive = appHistory.location.search === `?${ownerFilter(user)}`;
        links.push({
            id: 'chat-open',
            label: 'Your threads',
            to: `/inbox/chat/open?${ownerFilter(user)}`,
            count: userInboxCount && userInboxCount.count ? userInboxCount.count : 0,
            activeMatch: /^\/inbox\/chat\/(?!unassigned|chatbot)[\w-]+(?:\/.*)?$/,
            searchMatch: new RegExp(
                `filter%5Bowner%5D%5BanyAll%5D=any&filter%5Bowner%5D%5Bdata%5D%5B0%5D%5Bdata%5D=${user.id}(?:$|&)`
            ),
            icon: () => getAvatar(user, isActive),
        });
    }

    if (!options?.excludeChatbot) {
        links.push({
            id: 'chat-chatbot',
            label: 'Chatbot',
            to: `/inbox/chat/chatbot?${periodFilter()}`,
            count: chatCounts ? chatCounts.chatbotCount : 0,
            activeMatch: /^\/inbox\/chat\/chatbot(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
            // @ts-expect-error
            icon: () => <ChatbotIcon color={colors.white} alignCenter={true} size={17} />,
        });
    }

    return links;
};

export const getMessageLinks = (
    smsCounts: SmsThreadCounts | null | undefined,
    userCanAccessSettings: boolean,
    user?: User | null
): SubmenuNavItem[] => {
    const links: SubmenuNavItem[] = [];

    if (userCanAccessSettings) {
        links.push({
            id: 'sms-all',
            label: 'All',
            to: `/inbox/sms/open?${periodFilter()}`,
            count: smsCounts ? smsCounts.totalCount : 0,
            activeMatch: /^\/inbox\/sms\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
            // @ts-expect-error
            icon: () => <PeopleRegularIcon size={16} color={colors.white} />,
        });
    }

    links.push({
        id: 'sms-unassigned',
        label: 'Unassigned',
        to: `/inbox/sms/unassigned?${periodFilter()}`,
        count: smsCounts ? smsCounts.unassignedCount : 0,
        activeMatch: /^\/inbox\/sms\/unassigned(?:\/.*)?$/,
        searchMatch: /filter%5Bperiod%5D=/,
        // @ts-expect-error
        icon: () => <PersonRoundRegularIcon size={19} color={colors.white} />,
    });

    if (user) {
        const userInboxCount =
            smsCounts &&
            smsCounts.userInboxCounts.find(
                (userInbox) => userInbox.user && userInbox.user.id === user.id
            );

        const isActive = appHistory.location.search === `?${ownerFilter(user)}`;
        links.push({
            id: 'sms-open',
            label: 'Your threads',
            to: `/inbox/sms/open?${ownerFilter(user)}`,
            count: userInboxCount && userInboxCount.count ? userInboxCount.count : 0,
            activeMatch: /^\/inbox\/sms\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: new RegExp(
                `filter%5Bowner%5D%5BanyAll%5D=any&filter%5Bowner%5D%5Bdata%5D%5B0%5D%5Bdata%5D=${user.id}(?:$|&)`
            ),
            icon: () => getAvatar(user, isActive),
        });
    }

    return links;
};

export const getMessengerLinks = (
    messengerCounts: MessengerThreadCounts | null | undefined,
    userCanAccessSettings: boolean,
    user?: User | null
): SubmenuNavItem[] => {
    const links: SubmenuNavItem[] = [];

    if (userCanAccessSettings) {
        links.push({
            id: 'messenger-all',
            label: 'All',
            to: `/inbox/messenger/open?${periodFilter()}`,
            count: messengerCounts ? messengerCounts.totalCount : 0,
            activeMatch: /^\/inbox\/messenger\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
            // @ts-expect-error
            icon: () => <PeopleRegularIcon size={16} color={colors.white} />,
        });
    }

    links.push({
        id: 'messenger-unassigned',
        label: 'Unassigned',
        to: `/inbox/messenger/unassigned?${periodFilter()}`,
        count: messengerCounts ? messengerCounts.unassignedCount : 0,
        activeMatch: /^\/inbox\/messenger\/unassigned(?:\/.*)?$/,
        searchMatch: /filter%5Bperiod%5D=/,
        // @ts-expect-error
        icon: () => <PersonRoundRegularIcon size={19} color={colors.white} />,
    });

    if (user) {
        const userInboxCount =
            messengerCounts &&
            messengerCounts.userInboxCounts.find(
                (userInbox) => userInbox.user && userInbox.user.id === user.id
            );

        const isActive = appHistory.location.search === `?${ownerFilter(user)}`;
        links.push({
            id: 'messenger-open',
            label: 'Your threads',
            to: `/inbox/messenger/open?${ownerFilter(user)}`,
            count: userInboxCount && userInboxCount.count ? userInboxCount.count : 0,
            activeMatch: /^\/inbox\/messenger\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: new RegExp(
                `filter%5Bowner%5D%5BanyAll%5D=any&filter%5Bowner%5D%5Bdata%5D%5B0%5D%5Bdata%5D=${user.id}(?:$|&)`
            ),
            icon: () => getAvatar(user, isActive),
        });
    }

    return links;
};

export const getInstagramLinks = (
    instagramCounts: InstagramThreadCounts | null | undefined,
    userCanAccessSettings: boolean,
    user?: User | null
): SubmenuNavItem[] => {
    const links: SubmenuNavItem[] = [];

    if (userCanAccessSettings) {
        links.push({
            id: 'instagram-all',
            label: 'All',
            to: `/inbox/instagram/open?${periodFilter()}`,
            count: instagramCounts ? instagramCounts.totalCount : 0,
            activeMatch: /^\/inbox\/instagram\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
            // @ts-expect-error
            icon: () => <PeopleRegularIcon size={16} color={colors.white} />,
        });
    }

    links.push({
        id: 'instagram-unassigned',
        label: 'Unassigned',
        to: `/inbox/instagram/unassigned?${periodFilter()}`,
        count: instagramCounts ? instagramCounts.unassignedCount : 0,
        activeMatch: /^\/inbox\/instagram\/unassigned(?:\/.*)?$/,
        searchMatch: /filter%5Bperiod%5D=/,
        // @ts-expect-error
        icon: () => <PersonRoundRegularIcon size={19} color={colors.white} />,
    });

    if (user) {
        const userInboxCount =
            instagramCounts &&
            instagramCounts.userInboxCounts.find(
                (userInbox) => userInbox.user && userInbox.user.id === user.id
            );

        const isActive = appHistory.location.search === `?${ownerFilter(user)}`;
        links.push({
            id: 'instagram-open',
            label: 'Your threads',
            to: `/inbox/instagram/open?${ownerFilter(user)}`,
            count: userInboxCount && userInboxCount.count ? userInboxCount.count : 0,
            activeMatch: /^\/inbox\/instagram\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: new RegExp(
                `filter%5Bowner%5D%5BanyAll%5D=any&filter%5Bowner%5D%5Bdata%5D%5B0%5D%5Bdata%5D=${user.id}(?:$|&)`
            ),
            icon: () => getAvatar(user, isActive),
        });
    }

    return links;
};

export const getWhatsAppLinks = (
    whatsAppCounts: WhatsAppThreadCounts | null | undefined,
    userCanAccessSettings: boolean,
    user?: User | null
): SubmenuNavItem[] => {
    const links: SubmenuNavItem[] = [];

    if (userCanAccessSettings) {
        links.push({
            id: 'whatsapp-all',
            label: 'All',
            to: `/inbox/whatsapp/open?${periodFilter()}`,
            count: whatsAppCounts ? whatsAppCounts.totalCount : 0,
            activeMatch: /^\/inbox\/whatsapp\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
            // @ts-expect-error
            icon: () => <PeopleRegularIcon size={16} color={colors.white} />,
        });
    }

    links.push({
        id: 'whatsapp-unassigned',
        label: 'Unassigned',
        to: `/inbox/whatsapp/unassigned?${periodFilter()}`,
        count: whatsAppCounts ? whatsAppCounts.unassignedCount : 0,
        activeMatch: /^\/inbox\/whatsapp\/unassigned(?:\/.*)?$/,
        searchMatch: /filter%5Bperiod%5D=/,
        // @ts-expect-error
        icon: () => <PersonRoundRegularIcon size={19} color={colors.white} />,
    });

    if (user) {
        const userInboxCount =
            whatsAppCounts &&
            whatsAppCounts.userInboxCounts.find(
                (userInbox) => userInbox.user && userInbox.user.id === user.id
            );

        const isActive = appHistory.location.search === `?${ownerFilter(user)}`;
        links.push({
            id: 'whatsapp-open',
            label: 'Your threads',
            to: `/inbox/whatsapp/open?${ownerFilter(user)}`,
            count: userInboxCount && userInboxCount.count ? userInboxCount.count : 0,
            activeMatch: /^\/inbox\/whatsapp\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: new RegExp(
                `filter%5Bowner%5D%5BanyAll%5D=any&filter%5Bowner%5D%5Bdata%5D%5B0%5D%5Bdata%5D=${user.id}(?:$|&)`
            ),
            icon: () => getAvatar(user, isActive),
        });
    }

    return links;
};

export const getEmailLinks = (
    emailCounts: EmailThreadCounts | null | undefined,
    userCanAccessSettings: boolean,
    user?: User | null
): SubmenuNavItem[] => {
    const links: SubmenuNavItem[] = [];

    if (userCanAccessSettings) {
        links.push({
            id: 'email-all',
            label: 'All',
            to: `/inbox/email/open?${periodFilter()}`,
            count: emailCounts ? emailCounts.totalCount : 0,
            activeMatch: /^\/inbox\/email\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
            // @ts-expect-error
            icon: () => <PeopleRegularIcon size={16} color={colors.white} />,
        });
    }

    links.push({
        id: 'email-unassigned',
        label: 'Unassigned',
        to: `/inbox/email/unassigned?${periodFilter()}`,
        count: emailCounts ? emailCounts.unassignedCount : 0,
        activeMatch: /^\/inbox\/email\/unassigned(?:\/.*)?$/,
        searchMatch: /filter%5Bperiod%5D=/,
        // @ts-expect-error
        icon: () => <PersonRoundRegularIcon size={19} color={colors.white} />,
    });

    if (user) {
        const userInboxCount =
            emailCounts &&
            emailCounts.userInboxCounts.find(
                (userInbox) => userInbox.user && userInbox.user.id === user.id
            );

        const isActive = appHistory.location.search === `?${ownerFilter(user)}`;
        links.push({
            id: 'email-open',
            label: 'Your threads',
            to: `/inbox/email/open?${ownerFilter(user)}`,
            count: userInboxCount && userInboxCount.count ? userInboxCount.count : 0,
            activeMatch: /^\/inbox\/email\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: new RegExp(
                `filter%5Bowner%5D%5BanyAll%5D=any&filter%5Bowner%5D%5Bdata%5D%5B0%5D%5Bdata%5D=${user.id}(?:$|&)`
            ),
            icon: () => getAvatar(user, isActive),
        });
    }

    return links;
};

export const getAllChannelsLinks = (
    allChannelsCounts: AllChannelsThreadCounts | null | undefined,
    userCanAccessSettings: boolean,
    user?: User | null
): SubmenuNavItem[] => {
    const links: SubmenuNavItem[] = [];

    if (userCanAccessSettings) {
        links.push({
            id: 'all-channels-all',
            label: 'All',
            to: `/inbox/all/open?${periodFilter()}`,
            count: allChannelsCounts ? allChannelsCounts.totalCount : 0,
            activeMatch: /^\/inbox\/all\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
            // @ts-expect-error
            icon: () => <PeopleRegularIcon size={16} color={colors.white} />,
        });
    }

    links.push({
        id: 'all-channels-unassigned',
        label: 'Unassigned',
        to: `/inbox/all/unassigned?${periodFilter()}`,
        count: allChannelsCounts ? allChannelsCounts.unassignedCount : 0,
        activeMatch: /^\/inbox\/all\/unassigned(?:\/.*)?$/,
        searchMatch: /filter%5Bperiod%5D=/,
        // @ts-expect-error
        icon: () => <PersonRoundRegularIcon size={19} color={colors.white} />,
    });

    if (user) {
        const userInboxCount =
            allChannelsCounts &&
            allChannelsCounts.userInboxCounts.find(
                (userInbox) => userInbox.user && userInbox.user.id === user.id
            );

        const isActive = appHistory.location.search === `?${ownerFilter(user)}`;
        links.push({
            id: 'all-channels-open',
            label: 'Your threads',
            to: `/inbox/all/open?${ownerFilter(user)}`,
            count: userInboxCount && userInboxCount.count ? userInboxCount.count : 0,
            activeMatch: /^\/inbox\/all\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: new RegExp(
                `filter%5Bowner%5D%5BanyAll%5D=any&filter%5Bowner%5D%5Bdata%5D%5B0%5D%5Bdata%5D=${user.id}(?:$|&)`
            ),
            icon: () => getAvatar(user, isActive),
        });
    }

    return links;
};

const getAvatar = (user: User, isActive: boolean) => {
    return (
        <>
            {user.avatarUrl && (
                <div className='flex align-center justify-center'>
                    <img
                        src={`${user.avatarUrl}?width=100&height=100`}
                        className={styles['avatar-image']}
                    />
                </div>
            )}
            {user.initials && (
                <div className={isActive ? styles['initials--active'] : styles.initials}>
                    {user.initials}
                </div>
            )}
        </>
    );
};
